// Utility to format and convert Splinterlands card prices to DEC
// Ensures consistent formatting across CardItem, CardDetailsDialog, and Market
import priceService from '../../services/PriceService';

// Constants for price conversion
const USD_TO_DEC_FIXED_RATE = 1000; // Default fixed rate (1 USD = 1000 DEC)

/**
 * Gets the current conversion rate of USD to DEC based on market prices
 * @returns {number} - Current conversion rate (how many DEC per 1 USD)
 */
export function getCurrentUsdToDecRate() {
  // Get current DEC price from PriceService
  const { prices } = priceService.getData();
  
  if (!prices || !prices.dec || prices.dec <= 0) {
    // Fallback to fixed rate if no price available
    return USD_TO_DEC_FIXED_RATE;
  }
  
  // Calculate how many DEC = 1 USD based on current market price
  // For example, if 1 DEC = $0.001, then 1 USD = 1000 DEC
  const rate = 1 / prices.dec;
  
  return rate;
}

/**
 * Converts a price from USD to DEC using current market rates
 * @param {number|string} usdPrice - The price in USD
 * @returns {number} - The price converted to DEC
 */
export function convertUsdToDec(usdPrice) {
  if (usdPrice === undefined || usdPrice === null || isNaN(usdPrice)) return 0;
  const usdToDecRate = getCurrentUsdToDecRate();
  // Keine Rundung auf ganze Zahl, sondern auf 3 Nachkommastellen (englische Schreibweise)
  return Number((Number(usdPrice) * usdToDecRate).toFixed(3));
}

/**
 * Converts a price from USD to CREDITS using fixed rate
 * @param {number|string} usdPrice - The price in USD
 * @returns {number} - The price converted to CREDITS (1000 CREDITS = 1 USD)
 */
export function convertUsdToCredits(usdPrice) {
  if (usdPrice === undefined || usdPrice === null || isNaN(usdPrice)) return 0;
  // Fixed rate: 1000 CREDITS = 1 USD
  const creditsAmount = Number(usdPrice) * 1000;
  return Number(creditsAmount.toFixed(0)); // Credits are usually whole numbers
}

/**
 * Formats a price in DEC with English thousands separators.
 * @param {number|string} price - The price value (should already be in DEC format)
 * @param {boolean} isDec - Legacy parameter, kept for backward compatibility
 * @returns {string} - Formatted price string in DEC
 */
export function formatPriceToDec(price, isDec = false) {
  if (price === undefined || price === null || isNaN(price)) return '0 DEC';
  let decPrice = Number(price);
  // Always use English formatting: period as decimal separator, comma as thousands separator
  const formatted = decPrice % 1 === 0
    ? Math.round(decPrice).toLocaleString('en-US')
    : decPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  return `${formatted} DEC`;
}

/**
 * Formats a price in USD with English thousands separators.
 * @param {number|string} price - The price value (should already be in USD format)
 * @returns {string} - Formatted price string in USD
 */
export function formatPriceToUsd(price) {
  if (price === undefined || price === null || isNaN(price)) return '$0.00';
  let usdPrice = Number(price);
  // Always use English formatting: period as decimal separator, comma as thousands separator
  return `$${usdPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Formats a price in CREDITS with English thousands separators.
 * @param {number|string} price - The price value (should already be in CREDITS format)
 * @returns {string} - Formatted price string in CREDITS
 */
export function formatPriceToCredits(price) {
  if (price === undefined || price === null || isNaN(price)) return '0 CREDITS';
  let creditsPrice = Number(price);
  // Always use English formatting: period as decimal separator, comma as thousands separator
  const formatted = creditsPrice % 1 === 0
    ? Math.round(creditsPrice).toLocaleString('en-US')
    : creditsPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `${formatted} CREDITS`;
}
